/**
 * onisa-cart-pack-swap.js  — v3.1 (23/09/2026) · v3 (06/09/2026, auditoría B01)
 *
 * ARQUITECTURA: "Fila virtual" — el carrito puede contener Pack de 2 + 1 Unidad
 * internamente, pero el usuario SIEMPRE ve UNA fila por producto/color/talla con:
 *   • Cantidad total en unidades individuales (no en "packs")
 *   • Precio original tachado  +  precio real (el que cobra el checkout)
 *   • Botones ± que ajustan unidades individuales y recalculan el mix óptimo de packs
 *
 * Algoritmo greedy (pack más grande primero) + regla "unidad de regalo":
 *   qty=1 → 1×unitaria
 *   qty=2 → 1×Pack de 2
 *   qty=3 → 1×Pack de 2 + 1×unitaria
 *   qty=4 → 1×Pack de 4 (o 2×Pack de 2 si no existe)
 *   Si existe un pack de qty+1 unidades que cuesta lo mismo o menos que la mejor
 *   combinación de qty, se usa ese pack: la unidad extra sale gratis y el cliente
 *   NUNCA paga más de lo que ha pedido.
 *
 * Cambios v3 (fallos reproducidos en producción el 06/09/2026):
 *   1. El pie de /cart (total, envío) y la burbuja del header no se refrescaban tras
 *      un cambio: la fila decía 269,85 € y el total 159,90 € (el checkout cobraba 269,85).
 *      Ahora UNA sola llamada a /cart/update devuelve las secciones (página, drawer,
 *      burbuja) y todas se repintan desde la MISMA respuesta.
 *   2. La regla "unidad gratis" comparaba contra qty × precio unitario: con Pack Pareja
 *      (−20 €) subía 3 → 4 fundas cobrando 20 € más que 2+1, y el «−» rebotaba a 4
 *      (bucle entre el render de la página y el del drawer, que compartían estado).
 *      Ahora compara contra la combinación óptima y el render nunca dispara cambios
 *      a partir del estado de OTRO contenedor.
 *   3. La fila mostraba el precio de la combinación ÓPTIMA aunque el carrito llevara
 *      otra (2 unidades sueltas: fila 159,90 €, checkout 179,90 €). Ahora muestra lo
 *      que cobra el carrito y, si no es óptimo, lo reagrupa UNA vez de forma atómica.
 *   4. El cambio es atómico (un único /cart/update con ceros + destino). Antes eran
 *      ceros + N adds: un fallo intermedio vaciaba el grupo.
 *   5. Botón de pago deshabilitado mientras hay una actualización en vuelo; si falla,
 *      se recupera el estado real del servidor y se avisa en #cart-errors.
 *
 * Cambio v3.1 (23/09/2026, Bug 51): en el cajón solo se repintaban la lista y el total.
 *   El aviso «Te faltan X € para envío gratis», su barra, el texto del botón («Comprar con
 *   envío gratis») y el estado de carrito vacío se quedaban como al abrir el cajón: con 1→2
 *   coleteros el total pasaba a 23,90 € y el aviso seguía en 28,05 €; al vaciarlo con la
 *   papelera quedaba «Total 0,00 €» con el botón de pagar activo. Ahora se repinta el
 *   interior completo del cajón (.drawer__inner), igual que hace Dawn al cambiar la cantidad
 *   de un producto sin packs, y se vuelve a fijar el foco dentro del cajón.
 *
 * Requiere data-pack-group / data-pack-size / data-pack-map / data-unit-variant /
 * data-unit-price en cada <tr> de carrito, generados por oc-pack-map.liquid.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
     UTILIDADES
  ───────────────────────────────────────────────────────────────────────── */

  function parseJSON(str, fallback) {
    try { return JSON.parse(str || ''); } catch (_) { return fallback === undefined ? {} : fallback; }
  }

  /** Formatea céntimos como "49,95 €" */
  function eur(cents) {
    return (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
  }

  function routeCartUpdate() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update';
  }
  function routeCart() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /** packMap {"2":{"id":X,"price":Y},"4":{...}} → [{packSize,id,price}] de mayor a menor */
  function packList(packMap) {
    return Object.keys(packMap).map(function (k) {
      var v = packMap[k];
      return { packSize: parseInt(k, 10), id: parseInt(v.id, 10), price: parseInt(v.price, 10) };
    }).sort(function (a, b) { return b.packSize - a.packSize; });
  }

  /** Greedy: pack más grande primero; el resto en unitarias. */
  function greedyAlloc(totalUnits, packs, unitVariantId, unitPriceCents) {
    var result = [];
    var rem = totalUnits;
    for (var i = 0; i < packs.length; i++) {
      var p = packs[i];
      if (rem >= p.packSize) {
        var n = Math.floor(rem / p.packSize);
        result.push({ variantId: p.id, qty: n, packSize: p.packSize, priceEach: p.price });
        rem -= n * p.packSize;
      }
    }
    if (rem > 0) {
      result.push({ variantId: parseInt(unitVariantId, 10), qty: rem, packSize: 1, priceEach: parseInt(unitPriceCents, 10) });
    }
    return result;
  }

  function allocCost(alloc) {
    return alloc.reduce(function (sum, a) { return sum + a.qty * a.priceEach; }, 0);
  }
  function allocUnits(alloc) {
    return alloc.reduce(function (sum, a) { return sum + a.qty * a.packSize; }, 0);
  }

  /**
   * Asignación óptima para totalUnits.
   * Regla "unidad de regalo": si hay un pack de totalUnits+1 unidades cuyo precio es
   * ≤ el coste de la mejor combinación de totalUnits, se devuelve ese pack (misma
   * cuenta o menor, una unidad más). v2 comparaba contra totalUnits × precio unitario:
   * con Pack Pareja (−20 €) eso convertía 3 fundas (2+1 = 249,85 €) en 4 (269,85 €),
   * es decir, cobraba 20 € MÁS de lo que el cliente había pedido.
   */
  function optimalAlloc(totalUnits, packMap, unitVariantId, unitPriceCents) {
    var packs = packList(packMap);
    var best = greedyAlloc(totalUnits, packs, unitVariantId, unitPriceCents);
    var bestCost = allocCost(best);
    for (var i = 0; i < packs.length; i++) {
      var up = packs[i];
      if (up.packSize === totalUnits + 1 && up.price <= bestCost) {
        return [{ variantId: up.id, qty: 1, packSize: up.packSize, priceEach: up.price }];
      }
    }
    return best;
  }

  /** {variantId: qty} sin ceros */
  function allocMap(alloc) {
    var m = {};
    alloc.forEach(function (a) { if (a.qty > 0) m[a.variantId] = (m[a.variantId] || 0) + a.qty; });
    return m;
  }
  function sameAlloc(a, b) {
    var ma = allocMap(a), mb = allocMap(b);
    var ka = Object.keys(ma), kb = Object.keys(mb);
    if (ka.length !== kb.length) return false;
    return ka.every(function (k) { return ma[k] === mb[k]; });
  }
  function allocSignature(alloc) {
    var m = allocMap(alloc);
    return Object.keys(m).sort().map(function (k) { return k + ':' + m[k]; }).join('|');
  }

  /** Lo que hay AHORA en el carrito para este grupo, leído de las filas reales. */
  function rowsAlloc(rows, packMap, unitPriceCents) {
    var packs = packList(packMap);
    return rows.map(function (r) {
      var inp = r.querySelector('.quantity__input');
      var qty = inp ? (parseInt(inp.value, 10) || 0) : 0;
      var vid = parseInt((inp && inp.dataset.quantityVariantId) || '0', 10);
      var size = parseInt(r.dataset.packSize || '1', 10);
      var price = parseInt(unitPriceCents, 10);
      for (var i = 0; i < packs.length; i++) if (packs[i].id === vid) price = packs[i].price;
      return { variantId: vid, qty: qty, packSize: size, priceEach: price };
    }).filter(function (a) { return a.qty > 0; });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     ESTILOS
  ───────────────────────────────────────────────────────────────────────── */

  function injectCSS() {
    if (document.getElementById('oc-pswap-css')) return;
    var s = document.createElement('style');
    s.id = 'oc-pswap-css';
    s.textContent = [
      /* Filas reales ocultas tras crear la fila virtual */
      '.oc-pack-real { display: none !important; }',
      /* Precio tachado en fila virtual */
      '.oc-vrow .oc-price-old {',
      '  text-decoration: line-through;',
      '  color: #666666;',
      '  font-size: 0.9em;',
      '  margin-right: 6px;',
      '}',
      '.oc-vrow .oc-price-new { font-weight: 600; color: #0e0e0e; }',
      /* Ocultar la opción "Pack de X" / "Cantidad" en el detalle de la fila virtual */
      '.oc-vrow .oc-option-pack { display: none !important; }',
    ].join('\n');
    document.head.appendChild(s);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     ESTADO
  ───────────────────────────────────────────────────────────────────────── */

  var _busy = false;
  /* groupKey → última petición del usuario que llegó con una actualización en vuelo */
  var _pending = {};
  /* groupKey → firma del estado real que ya se intentó reagrupar (una sola vez por estado) */
  var _autoFixSig = {};

  /* ─────────────────────────────────────────────────────────────────────────
     DETECCIÓN DE GRUPOS
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Filas REALES agrupadas por data-pack-group DENTRO de un contenedor.
   * Excluye filas virtuales (data-pack-vkey) para evitar bucles.
   */
  function collectGroups(container) {
    var rows = (container || document).querySelectorAll('tr[data-pack-group]:not([data-pack-vkey])');
    var map = {};
    for (var i = 0; i < rows.length; i++) {
      var key = rows[i].dataset.packGroup;
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(rows[i]);
    }
    return map;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     RENDERIZADO DE FILA VIRTUAL
  ───────────────────────────────────────────────────────────────────────── */

  function renderVirtualRow(groupKey, rows) {
    var anchor = rows[0];

    /* Eliminar fila virtual anterior para este grupo SOLO en el mismo tbody
       (la página /cart y el drawer tienen tablas separadas). */
    var tbody = anchor.parentElement;
    var existing = tbody.querySelectorAll('[data-pack-vkey]');
    for (var x = 0; x < existing.length; x++) {
      if (existing[x].dataset.packVkey === groupKey) existing[x].remove();
    }

    /* SEGURIDAD: filas reales visibles ANTES de intentar crear la virtual. Si la
       creación falla, el usuario ve el producto real, nunca una fila fantasma. */
    rows.forEach(function (r) { r.classList.remove('oc-pack-real'); });

    var packMap       = parseJSON(anchor.dataset.packMap, {});
    var unitVariantId = anchor.dataset.unitVariant;
    var unitPrice     = parseInt(anchor.dataset.unitPrice || '0', 10);

    /* Estado REAL del grupo (lo que cobra el checkout) */
    var real       = rowsAlloc(rows, packMap, unitPrice);
    var totalUnits = allocUnits(real);
    /* Sin unidades (input aún no poblado): filas reales visibles y fuera. */
    if (totalUnits === 0) return;

    var realCost = allocCost(real);

    /* Si el carrito no lleva la combinación óptima (p. ej. 2 unidades sueltas en vez
       de un Pack de 2, o 3 sueltas cuando 3+1 sale por lo mismo), se reagrupa UNA vez
       por estado. Mientras tanto se pinta lo REAL: nunca un precio que el checkout no cobre. */
    var optimal = optimalAlloc(totalUnits, packMap, unitVariantId, unitPrice);
    if (!sameAlloc(real, optimal) && !_busy) {
      var sig = allocSignature(real);
      if (_autoFixSig[groupKey] !== sig) {
        _autoFixSig[groupKey] = sig;
        setTimeout(function () { fireGroupUpdate(groupKey, rows, totalUnits, { auto: true }); }, 0);
      }
    }

    var actualCents = realCost;
    var fullCents   = totalUnits * unitPrice;
    var hasDiscount = unitPrice > 0 && actualCents < fullCents;

    /* Clonar la primera fila real como plantilla */
    var vrow = anchor.cloneNode(true);
    vrow.classList.add('oc-vrow');
    vrow.dataset.packVkey = groupKey;
    vrow.removeAttribute('id');

    /* ── 1. Input de cantidad: total de unidades ── */
    var qInput = vrow.querySelector('.quantity__input');
    if (qInput) {
      qInput.value = totalUnits;
      qInput.setAttribute('value', totalUnits);
      qInput.dataset.packVkey = groupKey;
      qInput.setAttribute('min', '0');
      qInput.setAttribute('max', '20');
      qInput.setAttribute('step', '1');
      /* Evitar que Dawn lo asocie a una línea concreta */
      qInput.removeAttribute('data-index');
    }

    /* ── 2. Ocultar opción "Pack" / "Cantidad" ── */
    var opts = vrow.querySelectorAll('.product-option');
    for (var j = 0; j < opts.length; j++) {
      var dt = opts[j].querySelector('dt');
      if (dt) {
        var label = dt.textContent.trim();
        if (label === 'Pack:' || label === 'Cantidad:') opts[j].classList.add('oc-option-pack');
      }
    }

    /* ── 3. Precio unitario en el panel de detalles (móvil) ── */
    var priceHTML = hasDiscount
      ? '<s class="oc-price-old">' + eur(fullCents) + '</s>'
        + '<strong class="oc-price-new">' + eur(actualCents) + '</strong>'
      : '<span>' + eur(actualCents) + '</span>';

    var detailPrices = vrow.querySelectorAll('.cart-item__discounted-prices, .cart-item__price-wrapper');
    for (var dp = 0; dp < detailPrices.length; dp++) {
      if (!detailPrices[dp].closest('.cart-item__totals')) detailPrices[dp].innerHTML = priceHTML;
    }

    /* ── 4. Precio total (columna derecha) — Dawn genera varias celdas por breakpoint ── */
    var totalHTML = hasDiscount
      ? '<div class="cart-item__price-wrapper">'
        + '<s class="oc-price-old">' + eur(fullCents) + '</s>'
        + '<strong class="oc-price-new">' + eur(actualCents) + '</strong>'
        + '</div>'
      : '<div class="cart-item__price-wrapper">'
        + '<span class="price price--end">' + eur(actualCents) + '</span>'
        + '</div>';

    var totalsCells = vrow.querySelectorAll('.cart-item__totals');
    for (var tc = 0; tc < totalsCells.length; tc++) totalsCells[tc].innerHTML = totalHTML;

    /* ── 5. Botón "Eliminar": interceptar ANTES de que cart-remove-button actúe ── */
    var removeBtn = vrow.querySelector('cart-remove-button');
    if (removeBtn) {
      removeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        fireGroupUpdate(groupKey, rows, 0);
      });
    }

    /* ── 6. Limpiar clase heredada del clon ── */
    vrow.classList.remove('oc-pack-real');

    /* ── 7. Imágenes del clon sin lazy (ya están cargadas en la real) ── */
    var vImgs = vrow.querySelectorAll('img');
    for (var vi = 0; vi < vImgs.length; vi++) vImgs[vi].removeAttribute('loading');

    /* ── 8. Insertar la virtual antes de la primera real ── */
    anchor.parentElement.insertBefore(vrow, anchor);

    /* ── 9. Ocultar reales DESPUÉS de que la virtual está en el DOM ── */
    rows.forEach(function (r) { r.classList.add('oc-pack-real'); });

    /* ── 10. Cambios de cantidad en el input virtual (capture: antes que QuantityInput/Dawn) ── */
    if (qInput) {
      qInput.addEventListener('change', function (e) {
        e.stopImmediatePropagation();
        e.stopPropagation();
        var val = parseInt(this.value, 10);
        if (!isNaN(val) && val >= 0) fireGroupUpdate(groupKey, rows, val);
      }, true);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     SUPERFICIES DEL CARRITO (página, drawer, burbuja)
  ───────────────────────────────────────────────────────────────────────── */

  /** Secciones a repintar, con los ids reales del tema (Dawn: data-id = section.id). */
  function sectionTargets() {
    var t = [];
    var items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) t.push({ id: items.dataset.id, el: items, sel: '.js-contents' });
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) t.push({ id: footer.dataset.id, el: footer, sel: '.js-contents' });
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) t.push({ id: 'cart-icon-bubble', el: bubble, sel: '.shopify-section' });
    if (document.querySelector('cart-drawer')) t.push({ id: 'cart-drawer', drawer: true });
    return t;
  }

  /** Repinta cada superficie con el HTML de secciones devuelto por Shopify. */
  function paintSections(sections, targets) {
    targets.forEach(function (t) {
      var html = sections && sections[t.id];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      if (t.drawer) {
        /* Interior completo, mismo criterio que CartDrawerItems de Dawn al cambiar una cantidad.
           Con solo lista + total (v3) se quedaban congelados el aviso de envío gratis, el texto
           del botón de pago y el estado vacío, que viven fuera de esos dos bloques. */
        var dstInner = document.querySelector('#CartDrawer .drawer__inner');
        var srcInner = doc.querySelector('.drawer__inner');
        if (dstInner && srcInner) dstInner.innerHTML = srcInner.innerHTML;
        return;
      }
      var src = doc.querySelector(t.sel);
      var dst = t.el.querySelector(t.sel) || t.el;
      if (src && dst) dst.innerHTML = src.innerHTML;
    });
  }

  function toggleEmpty(itemCount) {
    if (typeof itemCount !== 'number') return;
    var empty = itemCount === 0;
    var ci = document.querySelector('cart-items');
    if (ci) ci.classList.toggle('is-empty', empty);
    var f = document.getElementById('main-cart-footer');
    if (f) f.classList.toggle('is-empty', empty);
    var d = document.querySelector('cart-drawer');
    if (d) d.classList.toggle('is-empty', empty);
  }

  /**
   * Tras repintar el interior del cajón, la trampa de foco de Dawn apunta a botones que ya no
   * existen (cerrar y pagar): se vuelve a fijar como hace Dawn, en el cajón vacío o en el cajón.
   * Solo con el cajón abierto: en /cart el cajón se repinta cerrado y no debe robar el foco.
   */
  function refocusDrawer() {
    var d = document.querySelector('cart-drawer');
    if (!d || !d.classList.contains('active') || typeof trapFocus !== 'function') return;
    var empty = d.querySelector('.drawer__inner-empty');
    if (empty) trapFocus(empty, d.querySelector('a') || empty);
    else trapFocus(d, d.querySelector('.drawer__inner') || d);
  }

  /** Estado real del servidor (ruta de error): secciones frescas + item_count. */
  function refreshFromServer(targets) {
    var ids = targets.map(function (t) { return t.id; });
    if (!ids.length) { window.location.reload(); return Promise.resolve(); }
    return fetch(routeCart() + '?sections=' + encodeURIComponent(ids.join(',')), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) { paintSections(sections, targets); })
      .then(function () { return fetch(routeCart() + '.js', { headers: { Accept: 'application/json' } }); })
      .then(function (r) { return r.json(); })
      .then(function (cart) { toggleEmpty(cart.item_count); refocusDrawer(); })
      .catch(function () { window.location.reload(); });
  }

  function setCheckoutDisabled(on) {
    var btns = document.querySelectorAll('#checkout, #CartDrawer-Checkout, button[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (on) {
        if (b.disabled) continue;            /* ya deshabilitado por Dawn (carrito vacío): no tocar */
        b.disabled = true;
        b.dataset.ocLocked = '1';
      } else if (b.dataset.ocLocked) {
        b.disabled = false;
        delete b.dataset.ocLocked;
      }
    }
  }

  function showError(msg) {
    var els = [document.getElementById('cart-errors'), document.getElementById('CartDrawer-CartErrors')];
    var text = msg || (window.cartStrings && window.cartStrings.error) || 'No se ha podido actualizar el carrito. Vuelve a intentarlo.';
    els.forEach(function (el) { if (el) el.textContent = text; });
  }
  function clearErrors() {
    var els = [document.getElementById('cart-errors'), document.getElementById('CartDrawer-CartErrors')];
    els.forEach(function (el) { if (el) el.textContent = ''; });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     ACTUALIZACIÓN DEL CARRITO
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Pone el grupo en totalUnits unidades con la combinación óptima de packs.
   * Una sola petición atómica a /cart/update: ceros para todas las variantes del grupo
   * + cantidades destino + secciones a repintar. opts.auto = reagrupación silenciosa
   * lanzada por el render (no es una petición del usuario, no se encola).
   */
  function fireGroupUpdate(groupKey, rows, totalUnits, opts) {
    opts = opts || {};
    if (_busy) {
      if (!opts.auto) _pending[groupKey] = { rows: rows, totalUnits: totalUnits };
      return;
    }
    _busy = true;
    setCheckoutDisabled(true);
    clearErrors();

    /* Feedback visual */
    var vrows = document.querySelectorAll('[data-pack-vkey="' + groupKey + '"]');
    for (var v = 0; v < vrows.length; v++) vrows[v].style.opacity = '0.4';

    var anchor    = rows[0];
    var packMap   = parseJSON(anchor.dataset.packMap, {});
    var unitVid   = parseInt(anchor.dataset.unitVariant, 10);
    var unitPrice = parseInt(anchor.dataset.unitPrice || '0', 10);

    /* updates = {variantId: qty}: ceros para todo el grupo, luego el destino */
    var updates = {};
    updates[unitVid] = 0;
    Object.keys(packMap).forEach(function (k) { updates[parseInt(packMap[k].id, 10)] = 0; });
    if (totalUnits > 0) {
      optimalAlloc(totalUnits, packMap, String(unitVid), unitPrice).forEach(function (a) {
        updates[a.variantId] = (updates[a.variantId] || 0) + a.qty;
      });
    }

    var targets = sectionTargets();
    var body = { updates: updates };
    var ids = targets.map(function (t) { return t.id; });
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }

    fetch(routeCartUpdate(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
    .then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw new Error((j && (j.description || j.message)) || ('HTTP ' + r.status));
        return j;
      });
    })
    .then(function (cart) {
      if (!targets.length) { window.location.reload(); return; }
      paintSections(cart.sections || {}, targets);
      toggleEmpty(cart.item_count);
      refocusDrawer();
    })
    .catch(function (err) {
      console.error('[oc-pack-swap]', err);
      showError(err && err.message && /[a-záéíóú]/i.test(err.message) && err.message.length < 160 ? err.message : null);
      return refreshFromServer(targets);
    })
    .finally(function () {
      _busy = false;
      setCheckoutDisabled(false);
      setTimeout(applyGrouping, 40);
      var p = _pending[groupKey];
      delete _pending[groupKey];
      if (p) fireGroupUpdate(groupKey, p.rows, p.totalUnits);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     APLICAR AGRUPACIÓN
  ───────────────────────────────────────────────────────────────────────── */

  /** Agrupa de forma independiente por contenedor (drawer y página tienen tablas distintas). */
  function applyGrouping() {
    var containers = Array.from(document.querySelectorAll('cart-drawer-items, cart-items'));
    if (containers.length === 0) containers = [null];

    containers.forEach(function (container) {
      var groups = collectGroups(container);
      Object.keys(groups).forEach(function (key) {
        renderVirtualRow(key, groups[key]);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     PARCHE A CartItems

     updateQuantity se resuelve por la cadena de prototipos EN EL MOMENTO DE LA
     LLAMADA, así que parchearlo en el prototipo funciona aunque el listener de
     onChange ya esté vinculado.
  ───────────────────────────────────────────────────────────────────────── */

  function patchCartItems() {
    if (typeof CartItems === 'undefined') return;
    if (CartItems.prototype._oniPswap3) return;
    CartItems.prototype._oniPswap3 = true;

    var _origUpdateQty = CartItems.prototype.updateQuantity;
    CartItems.prototype.updateQuantity = function (line, quantity, event, name, variantId) {
      if (event && event.target) {
        var tgt = event.target;
        /* Input de fila virtual: lo gestiona nuestro listener (defensa extra) */
        if (tgt.dataset && tgt.dataset.packVkey) return;
        /* Cualquier control dentro de la fila virtual (p. ej. "Eliminar", que Dawn engancha al clonar
           el custom element): lo gestiona el listener del grupo; si Dawn lanzara su /cart/change en
           paralelo, la línea ya no existiría y pintaría "Hubo un error" con el carrito bien vaciado. */
        if (tgt.closest && tgt.closest('[data-pack-vkey]')) return;
        /* Input de fila real oculta: ignorar */
        if (tgt.closest && tgt.closest('.oc-pack-real')) return;
      }
      return _origUpdateQty.call(this, line, quantity, event, name, variantId);
    };
  }

  /* ─────────────────────────────────────────────────────────────────────────
     OBSERVER: re-aplicar tras repintados del carrito (Dawn recrea nodos)
  ───────────────────────────────────────────────────────────────────────── */

  function startObserver() {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          /* Solo filas REALES de Dawn; las virtuales que insertamos no disparan otro ciclo. */
          if (node.querySelector && node.querySelector('[data-pack-group]:not([data-pack-vkey])')) {
            setTimeout(applyGrouping, 40);
            return; /* un solo disparo por lote de mutaciones */
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────────────────────────────── */

  function init() {
    injectCSS();

    if (typeof CartItems !== 'undefined') {
      patchCartItems();
    } else {
      customElements.whenDefined('cart-items').then(patchCartItems);
    }

    applyGrouping();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
